import { colomboBusinessDate, colomboClock, percentageOf, type OrderType } from "./core";
import type { AppliedDiscount, Coupon, OrderLine, OrderTotals, Promotion } from "./types";

export type ManualDiscount = { mode: "percent" | "fixed"; value: number; reason: string; lineId?: string | undefined };
export type PricingInput = {
  type: OrderType; lines: readonly OrderLine[]; taxBps: number; serviceChargeBps: number;
  /** Promotions that are already validated as applicable now (auto-applied plus cashier-selected). */
  promotions: readonly Promotion[]; coupon?: Coupon | undefined; manual: readonly ManualDiscount[];
  loyalty?: { points: number; pointValueMinor: number } | undefined;
};
export type PricingResult = { lines: OrderLine[]; discounts: AppliedDiscount[]; totals: OrderTotals; rejected: string[] };

export function promotionActiveAt(promotion: Promotion, now: Date): boolean {
  if (!promotion.active) return false;
  const date = colomboBusinessDate(now);
  if (promotion.startDate && date < promotion.startDate) return false;
  if (promotion.endDate && date > promotion.endDate) return false;
  const { minutes, weekday } = colomboClock(now);
  if (promotion.days.length > 0 && !promotion.days.includes(weekday)) return false;
  const { fromMinutes: from, toMinutes: to } = promotion;
  if (from === to) return true;
  return from < to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

const lineValue = (line: OrderLine) => line.unitPriceMinor * line.quantity;
const payable = (line: OrderLine) => !line.voided && !line.complimentary;
const eligible = (promotion: Promotion, line: OrderLine) =>
  (promotion.itemIds.length === 0 && promotion.categoryIds.length === 0) ||
  promotion.itemIds.includes(line.menuItemId) || promotion.categoryIds.includes(line.categoryId);
const cap = (amount: number, promotion: { maxDiscountMinor: number }) =>
  promotion.maxDiscountMinor > 0 ? Math.min(amount, promotion.maxDiscountMinor) : amount;

type Scenario = { lines: OrderLine[]; discounts: AppliedDiscount[]; rejected: string[]; total: number };

function evaluate(input: PricingInput, promotions: readonly Promotion[], withExtras: boolean): Scenario {
  const lines = input.lines.map((line) => ({ ...line, discountMinor: 0 }));
  const discounts: AppliedDiscount[] = [];
  const rejected: string[] = [];
  const remaining = (line: OrderLine) => lineValue(line) - line.discountMinor;
  /** Spreads an item-level amount across lines, never exceeding what is left on each line. */
  const allocate = (targets: OrderLine[], amount: number): number => {
    let left = amount;
    for (const line of targets) {
      const take = Math.min(left, remaining(line));
      line.discountMinor += take; left -= take;
      if (left === 0) break;
    }
    return amount - left;
  };

  for (const promotion of promotions) {
    const targets = lines.filter((line) => payable(line) && eligible(promotion, line));
    if (targets.length === 0) continue;
    let applied = 0;
    if (promotion.kind === "bogo") {
      const group = promotion.buyQty + promotion.getQty;
      if (promotion.buyQty < 1 || promotion.getQty < 1) continue;
      const units = targets.flatMap((line) => Array.from({ length: line.quantity }, () => line)).sort((a, b) => a.unitPriceMinor - b.unitPriceMinor);
      const free = Math.floor(units.length / group) * promotion.getQty;
      let budget = cap(units.slice(0, free).reduce((sum, line) => sum + line.unitPriceMinor, 0), promotion);
      for (const line of units.slice(0, free)) {
        const take = Math.min(budget, line.unitPriceMinor, remaining(line));
        line.discountMinor += take; budget -= take; applied += take;
      }
    } else if (promotion.kind === "combo") {
      if (promotion.itemIds.length < 2) continue;
      const perItem = promotion.itemIds.map((id) => lines.filter((line) => payable(line) && line.menuItemId === id));
      const sets = Math.min(...perItem.map((group) => group.reduce((sum, line) => sum + line.quantity, 0)));
      if (sets < 1) continue;
      const setPrice = perItem.reduce((sum, group) => sum + Math.min(...group.map((line) => line.unitPriceMinor)), 0);
      const saving = Math.max(0, setPrice - promotion.valueMinor) * sets;
      applied = allocate(perItem.flat(), cap(saving, promotion));
    } else if (promotion.scope === "item") {
      for (const line of targets) {
        const amount = promotion.valueBps > 0 ? percentageOf(remaining(line), promotion.valueBps) : promotion.valueMinor * line.quantity;
        const take = Math.min(amount, remaining(line), promotion.maxDiscountMinor > 0 ? promotion.maxDiscountMinor - applied : Infinity);
        line.discountMinor += take; applied += take;
      }
    } else continue;
    if (applied > 0) discounts.push({ kind: promotion.autoApply ? "auto" : "promotion", label: promotion.name, amountMinor: applied, scope: "item", promotionId: promotion.id });
  }

  if (withExtras) for (const manual of input.manual.filter((entry) => entry.lineId)) {
    const line = lines.find((entry) => entry.id === manual.lineId && payable(entry));
    if (!line) continue;
    const amount = Math.min(remaining(line), manual.mode === "percent" ? percentageOf(remaining(line), manual.value) : manual.value);
    line.discountMinor += amount;
    if (amount > 0) discounts.push({ kind: "manual", label: `Item discount · ${line.name}`, amountMinor: amount, scope: "item", reason: manual.reason, ...(manual.mode === "percent" ? { bps: manual.value } : {}) });
  }

  const base = lines.filter(payable).reduce((sum, line) => sum + remaining(line), 0);
  let left = base;
  const bill = (entry: Omit<AppliedDiscount, "scope" | "amountMinor">, amount: number) => {
    const take = Math.min(left, amount);
    if (take <= 0) return 0;
    left -= take; discounts.push({ ...entry, amountMinor: take, scope: "bill" });
    return take;
  };
  for (const promotion of promotions) {
    if (promotion.scope !== "bill" || promotion.kind === "bogo" || promotion.kind === "combo") continue;
    if (base < promotion.minSpendMinor) { if (!promotion.autoApply) rejected.push(`${promotion.name} needs a higher bill value.`); continue; }
    const amount = cap(promotion.valueBps > 0 ? percentageOf(left, promotion.valueBps) : promotion.valueMinor, promotion);
    bill({ kind: promotion.kind === "staff" ? "staff" : promotion.autoApply ? "auto" : "promotion", label: promotion.name, promotionId: promotion.id, ...(promotion.valueBps > 0 ? { bps: promotion.valueBps } : {}) }, amount);
  }
  if (withExtras) {
    const coupon = input.coupon;
    if (coupon) {
      if (base < coupon.minSpendMinor) rejected.push(`Coupon ${coupon.code} needs a higher bill value.`);
      else bill({ kind: "coupon", label: `Coupon ${coupon.code}`, couponId: coupon.id, code: coupon.code }, cap(coupon.mode === "percent" ? percentageOf(left, coupon.value) : coupon.value, coupon));
    }
    for (const manual of input.manual.filter((entry) => !entry.lineId)) {
      bill({ kind: "manual", label: "Bill discount", reason: manual.reason, ...(manual.mode === "percent" ? { bps: manual.value } : {}) }, manual.mode === "percent" ? percentageOf(left, manual.value) : manual.value);
    }
    if (input.loyalty && input.loyalty.points > 0 && input.loyalty.pointValueMinor > 0) {
      const usable = Math.min(input.loyalty.points, Math.floor(left / input.loyalty.pointValueMinor));
      if (usable > 0) bill({ kind: "loyalty", label: `Loyalty points (${usable})`, points: usable }, usable * input.loyalty.pointValueMinor);
    }
  }
  return { lines, discounts, rejected, total: discounts.reduce((sum, discount) => sum + discount.amountMinor, 0) };
}

/**
 * Authoritative order pricing in integer cents. Stackable promotions combine with coupons, manual
 * and loyalty discounts; a non-stackable promotion is only ever applied alone, and the customer
 * receives whichever alternative saves more.
 */
export function priceOrder(input: PricingInput): PricingResult {
  const stackable = input.promotions.filter((promotion) => promotion.stackable);
  let best = evaluate(input, stackable, true);
  for (const exclusive of input.promotions.filter((promotion) => !promotion.stackable)) {
    const alone = evaluate(input, [exclusive], false);
    if (alone.total > best.total) best = alone;
  }
  const active = best.lines.filter((line) => !line.voided);
  const grossMinor = active.reduce((sum, line) => sum + lineValue(line), 0);
  const complimentaryMinor = active.filter((line) => line.complimentary).reduce((sum, line) => sum + lineValue(line), 0);
  const lineDiscountMinor = best.discounts.filter((discount) => discount.scope === "item").reduce((sum, discount) => sum + discount.amountMinor, 0);
  const billDiscountMinor = best.discounts.filter((discount) => discount.scope === "bill").reduce((sum, discount) => sum + discount.amountMinor, 0);
  const subtotalMinor = grossMinor - complimentaryMinor - lineDiscountMinor - billDiscountMinor;
  const serviceChargeMinor = percentageOf(subtotalMinor, input.serviceChargeBps);
  const taxMinor = percentageOf(subtotalMinor, input.taxBps);
  return {
    lines: best.lines, discounts: best.discounts, rejected: best.rejected,
    totals: {
      grossMinor, complimentaryMinor, lineDiscountMinor, billDiscountMinor,
      discountMinor: lineDiscountMinor + billDiscountMinor, subtotalMinor, serviceChargeMinor, taxMinor,
      totalMinor: subtotalMinor + serviceChargeMinor + taxMinor,
      costMinor: active.reduce((sum, line) => sum + line.unitCostMinor * line.quantity, 0)
    }
  };
}
